import { useRef, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import ButtonComponent from "components/button/ButtonComponent";
import TextFieldComponent, { TextFieldRef } from "components/input/TextFieldComponent";
import LabelHeader from "components/Label/LabelHeader";
import LayoutMain from "components/Layout/LayoutMain";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MESSAGE } from "constants/validate";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { getUserStorage } from "util/local_storage";
import { REMARK_MAX_LENGTH, SLASH_FORMAT_DATE } from "constants/format";
import { useNavigate } from "react-router";
import routes from "constants/routes";
import TableEstResult from "features/car_model/components/TableEstResult";
import TableResultEstDiscount from "features/car_model/components/TableResultEstDiscount";
import CardCarModel from "features/car_model/components/CardCarModel";
import useEstResult from "features/car_model/hook/useEstResult";
import useScrollToError from "hooks/useScrollToError";
import "dayjs/locale/ja";

/**
 * CalendarIconComponent renders a calendar icon image.
 * This component is used as a custom icon for date pickers.
 */
function CalendarIconComponent() {
  return <img src="/img/icons/calendar.svg" alt="calendar-icon" style={{ padding: "0px" }} />;
}

/**
 * EstResult component renders the result page for an estimation result.
 */
export default function EstResult() {
  const {
    estResult,
    quoteByList,
    setEstResult,
    setEstResultByKey,
    setEstResultDetail,
    setEstPrcChecked,
    handleSubmit,
    estPrcChecked,
    isFromCarType,
    newQuoteMaker,
    tireSizeText,
    estCashback,
    setEstCashback,
  } = useEstResult();
  const user = getUserStorage();
  const navigate = useNavigate();

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const customerNameRef = useRef<TextFieldRef>(null);
  const customerTelNoRef = useRef<TextFieldRef>(null);
  const remarkRef = useRef<TextFieldRef>(null);
  const customerNameContainerRef = useRef<HTMLDivElement>(null);
  const scrollToCustomerName = useScrollToError(customerNameContainerRef);

  const manualValidate = () => {
    const invalidRemark = estResult.tbl_quote_detail_tire?.some(
      (el) => el.remarks.length > REMARK_MAX_LENGTH,
    );

    const invalidFee = [
      "hub_rust_prevention_cost",
      "nitrogen_inflation_cost",
      "rubber_stem_valve_cost",
      "waste_tire_cost",
    ].some((el) => (estResult[el as never] as string).length > 10);

    return (
      invalidRemark ||
      invalidFee ||
      [customerNameRef, customerTelNoRef, remarkRef].some((ref) => ref.current?.validate())
    );
  };

  const onSubmit = async () => {
    const isHasError = manualValidate();
    
    // Check if customer_name has error and scroll/focus to it
    if (customerNameRef.current?.validate() || errors.customer_name) {
      scrollToCustomerName();
    }
    
    if (!isHasError && Object.values(errors).every((error) => !error)) {
      await handleSubmit();
    }
    setErrors({});
  };

  return (
    <>
      <LayoutMain
        sx={{
          flexDirection: "column",
          gap: 1,
          paddingY: 0,
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: { sm: "center", xl: "space-between" },
            width: "100%",
            flexDirection: { sm: "column", xl: "row" },
            alignItems: { sm: "center", xl: "flex-start" },
            gap: { sm: 4, xl: "auto" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "9px" }}>
            <Box sx={{ display: "flex", gap: { xs: 2, lg: 7 } }}>
              <Box sx={{ display: "flex", flexDirection: "column", width: "160px" }}>
                <Typography
                  sx={{
                    fontSize: "31px",
                    color: "var(--gray-700)",
                    fontWeight: "medium",
                    textAlign: "center",
                  }}
                >
                  お見積表
                </Typography>
                <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, textAlign: "center" }}>
                  {dayjs().format(SLASH_FORMAT_DATE)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "3px", flex: 1 }}>
                <Box ref={customerNameContainerRef} sx={{ width: { xs: "100%", lg: "209px" } }}>
                  <LabelHeader
                    required
                    label="お客様氏名"
                    sxTypography={{ ml: 0 }}
                    sxBox={{
                      justifyContent: "center",
                      border: "1px solid var(--primary-500)",
                      backgroundColor: "var(--blue-400)",
                    }}
                  />
                  <TextFieldComponent
                    ref={customerNameRef}
                    value={estResult.customer_name}
                    sxInput={{
                      textAlign: "center",
                      fontSize: { xs: 16, md: 18, lg: 20 },
                      height: "50px",
                    }}
                    sxRoot={{ borderRadius: 0 }}
                    onChangeText={(value) => {
                      setEstResult((prev) => ({
                        ...prev,
                        customer_name: value,
                      }));
                    }}
                    validateOption={{
                      required: {
                        message: MESSAGE.EMPTY,
                      },
                      maxLength: {
                        message: MESSAGE.MAX_LENGTH(100),
                        value: 100,
                      },
                    }}
                    onError={(error) => setErrors((pre) => ({ ...pre, customer_name: error }))}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "209px" } }}>
                  <LabelHeader
                    label="電話番号"
                    sxTypography={{ ml: 0 }}
                    sxBox={{
                      justifyContent: "center",
                      border: "1px solid var(--primary-500)",
                      backgroundColor: "var(--blue-400)",
                    }}
                  />
                  <TextFieldComponent
                    ref={customerTelNoRef}
                    value={estResult.customer_tel_no}
                    type="tel"
                    sxInput={{ textAlign: "center", fontSize: { xs: 16, md: 18, lg: 20 } }}
                    sxRoot={{ borderRadius: 0 }}
                    onChangeText={(value) => {
                      setEstResult((prev) => ({
                        ...prev,
                        customer_tel_no: value,
                      }));
                    }}
                    validateOption={{
                      includeNumberOnly: {
                        message: MESSAGE.INCLUDE_NUMBER_ONLY,
                      },
                      halfWidth: {
                        message: MESSAGE.HALFWIDTH,
                      },
                      minLength: {
                        message: MESSAGE.MIN_LENGTH(10),
                        value: 10,
                      },
                      maxLength: {
                        message: MESSAGE.MAX_LENGTH(11),
                        value: 11,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: { xs: 2, lg: 7 } }}>
              <Box sx={{ width: "160px" }}>
                <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, textAlign: "center" }}>
                  {user?.storeName}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "3px", flex: 1 }}>
                <Box sx={{ width: { xs: "100%", lg: "209px" } }}>
                  <LabelHeader
                    label="担当者"
                    sxTypography={{ ml: 0 }}
                    sxBox={{ justifyContent: "center" }}
                  />
                  <Autocomplete
                    disablePortal
                    options={quoteByList}
                    sx={{
                      border: "1px solid var(--offwhite-250)",
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        py: "3px",
                        borderRadius: "0px",
                        width: "100%",
                      },
                      "& .MuiOutlinedInput-input": {
                        textAlign: "center",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid var(--gray-250)",
                      },
                    }}
                    renderInput={(inputParams) => <TextField {...inputParams} label="" />}
                    inputValue={estResult.quote_by}
                    value={estResult.quote_by ? { label: estResult.quote_by } : null}
                    noOptionsText="該当するデータが見つかりません"
                    onInputChange={(event, value) => {
                      setEstResultByKey("quote_by", value);
                    }}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "209px" } }}>
                  <LabelHeader
                    label="有効期限"
                    sxTypography={{ ml: 0 }}
                    sxBox={{ justifyContent: "center" }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
                    <DatePicker
                      label=""
                      sx={{
                        overflow: "hidden",
                        minWidth: "unset !important",
                        "& .MuiInputBase-root": {
                          borderRadius: 0,
                          border: 0,
                        },
                        "& .MuiOutlinedInput-input": {
                          textAlign: "center",
                          padding: "10.5px 0px 10.5px 46px",
                          fontSize: { xs: 16, md: 18, lg: 20 },
                        },
                        "& .MuiButtonBase-root": {
                          padding: "0px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid var(--gray-250)",
                        },
                      }}
                      value={dayjs(estResult.exp_date)}
                      slots={{ openPickerIcon: CalendarIconComponent }}
                      format={SLASH_FORMAT_DATE}
                      onChange={(newValue) => {
                        setEstResultByKey("exp_date", newValue?.toISOString() ?? "");
                      }}
                      minDate={dayjs(new Date())}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            </Box>
          </Box>
          <CardCarModel
            sx={{
              justifyContent: { xs: "center", lg: "flex-end" },
              width: { xs: "100%", md: "fit-content", xl: "auto" },
            }}
            backRoute={() => navigate(routes.mstProcFee)}
            isFromCarType={isFromCarType}
            newQuoteMaker={newQuoteMaker}
            tireSizeText={tireSizeText}
          />
        </Box>
      </LayoutMain>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          py: "30px",
          justifyContent: "center",
          flexDirection: "column",
          p: { xs: 2, md: 2.5, lg: 5 },
          gap: 3,
        }}
      >
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <TableResultEstDiscount
            estResult={estResult}
            setEstResultByKey={setEstResultByKey}
            estPrcChecked={estPrcChecked}
            setEstPrcChecked={(key, value) =>
              setEstPrcChecked((prev) => ({ ...prev, [key]: value }))
            }
            estCashback={estCashback ?? { cashback_period: 0, puncture: "0" }}
            setEstCashback={(key, value: number | string) => {
              setEstCashback((prev) =>
                prev
                  ? { ...prev, [key]: value }
                  : { cashback_period: 0, puncture: "0", [key]: value },
              );
            }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <ButtonComponent
            theme="dark"
            label="選択した商品で見積確定"
            sx={{ width: "max-content" }}
            onClick={onSubmit}
          />
          <Typography sx={{ fontSize: { xs: "16px", xl: "20px" } }} variant="h4">
            最終金額にキャッシュバックは計算されません
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            overflowX: "auto",
          }}
        >
          <TableEstResult
            rounding={Number(estResult.round_type)}
            quoteDetailTire={estResult.tbl_quote_detail_tire}
            setEstResultDetail={setEstResultDetail}
            cashbackPeriod={estCashback?.cashback_period}
          />
        </Box>
      </Box>
    </>
  );
}
